import React, { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useLocation, useNavigate } from 'react-router-dom'
import { useSWRConfig } from 'swr'
import useSWRMutation from 'swr/mutation'
import { match } from 'ts-pattern'

import { EmployerAddress } from '@frame/ui-components/address/employer-address'
import { ExitPromptType } from '@frame/ui-components/exit-prompt'
import { Footer } from '@frame/ui-components/footer'
import { FormErrors } from '@frame/ui-components/form-errors'
import { LoadingSpinner } from '@frame/ui-components/loading-spinner'
import { WidgetHeaderWithNavigation } from '@frame/ui-components/widget-header'
import { Body } from '@frame/ui-primitives/body'
import { Button } from '@frame/ui-primitives/button'
import { Checkbox } from '@frame/ui-primitives/checkbox'
import { H2 } from '@frame/ui-primitives/h2'
import { Input } from '@frame/ui-primitives/input'
import { P } from '@frame/ui-primitives/p'

import { Loading } from '@frame/components/screens/non-terminal/loading'
import { InternalEventType } from '@frame/lib/api/events/types'
import { DirectTask, EmploymentStatus, TrueworkJsStrategy, UpdateDirectTaskBody } from '@frame/lib/api/tasks/types'
import { useRootContext } from '@frame/lib/context'
import { useAutoNavigate } from '@frame/lib/hooks/useAutoNavigate'
import { useDirectTask } from '@frame/lib/hooks/useDirectTask'
import { useEventHandler } from '@frame/lib/hooks/useEventHandler'
import { useHeaderTitle } from '@frame/lib/hooks/useHeaderTitle'
import { useLoginItems } from '@frame/lib/hooks/useLoginItems'
import { useShowExitPrompt } from '@frame/lib/hooks/useShowExitPrompt'
import { useStrategies } from '@frame/lib/hooks/useStrategies'
import { useVerifyViaCredentials } from '@frame/lib/hooks/useVerifyViaCredentials'
import { CachedEmploymentDetails, useCachedInfoStore } from '@frame/lib/stores/useCachedInfoStore'
import { useStore } from '@frame/lib/stores/useStore'
import { cn } from '@frame/lib/utils/cn'
import { dateIsValid, endDateIsValid, formatCurrency, formatDate, formatDateWithSlash } from '@frame/lib/utils/format'
import { isSessionInTerminalState } from '@frame/lib/utils/session'
import { routes } from '@frame/routes'
import { DataAttribute } from '@frame/types'

import { PayTypeFields } from './pay-type-fields'
import { FormValues, PayUnit } from './types'
import { getErrorLabelForKey } from './utils'

const PRESENT_END_DATE = 'Present'

export function StatedInformation() {
  const location = useLocation()
  const navigate = useNavigate()
  const { mutate } = useSWRConfig()
  const { api } = useRootContext()
  const { mutateAndNavigate } = useAutoNavigate()
  const { handleEvent } = useEventHandler()
  const { documentsEnabled, bilEnabledStrategies } = useStrategies()
  const showExitPrompt = useShowExitPrompt()
  const { maxEmployments, tasks, docsForMortgageEnabled } = useStore(({ session }) => ({
    maxEmployments: session?.max_employments ?? 1,
    tasks: session?.tasks ?? [],
    docsForMortgageEnabled: session?.configuration.docs_for_mortgage_enabled ?? false,
  }))
  const { saveDetails, cachedEmploymentInfo } = useCachedInfoStore((store) => ({
    saveDetails: store.saveDetails,
    cachedEmploymentInfo: store.cachedEmploymentInfo,
  }))
  const task = useDirectTask(true)
  const title = useHeaderTitle()
  const { trigger: updateTask, isMutating } = useSWRMutation(
    ['update', 'employer', task?.id],
    (_id, { arg: { empId, body } }: { arg: { empId: string; body: UpdateDirectTaskBody } }) =>
      api.tasks.updateDirectTask(empId, body),
    {
      onSuccess: async () => {
        handleEvent(InternalEventType.EDITED_EMPLOYER)
        await mutate(['all', 'tasks'])
      },
    }
  )
  const endDateEnabled = !documentsEnabled || docsForMortgageEnabled

  const {
    handleSubmit,
    watch,
    setValue,
    resetField,
    reset,
    setError,
    control,
    clearErrors,
    formState: { isSubmitting, errors, submitCount, isDirty, isValid },
  } = useForm<FormValues>({
    defaultValues: {
      does_not_have_employer_address: false,
      employer_name: '',
      start_date: '',
      end_date: '',
      job_title: '',
      current_job: false,
      employer_address: {
        description: '',
        place_id: '',
      },
      pay_unit: PayUnit.ANNUALLY,
      current_gross_base_pay: '',
      hours_worked_per_week: '',
    },
  })
  const loginItemIds = useLoginItems()
  const [isLoading, setIsLoading] = useState(false)
  const verify = useVerifyViaCredentials()

  const shouldLoginToEntity = loginItemIds.length === 1

  function goToNext(updatedTask?: DirectTask) {
    const failedToUpdate = !updatedTask
    const singleStrategy = bilEnabledStrategies.length === 1

    if (failedToUpdate) {
      if (docsForMortgageEnabled) {
        navigate(routes.companySearch)
      }
      navigate(routes.selectVerificationMethod)
    } else if (singleStrategy && shouldLoginToEntity) {
      setIsLoading(true)
      verify(updatedTask.id)
    } else if (singleStrategy) {
      setIsLoading(true)
      const params = new URLSearchParams()
      params.set('q', updatedTask?.employer_name ?? '')
      navigate(`${routes.statedInformation}/${updatedTask.id}`, { replace: true })
      match(bilEnabledStrategies[0])
        .with(TrueworkJsStrategy.CREDENTIALS, () => {
          const path = updatedTask ? `${routes.companySearch}/${updatedTask.id}` : routes.companySearch
          navigate(`${path}?${params.toString()}`)
        })
        .with(TrueworkJsStrategy.DOCUMENTS, () => {
          const path = updatedTask ? `${routes.documents}/${updatedTask.id}` : routes.documents
          navigate(`${path}?${params.toString()}`)
        })
        .with(TrueworkJsStrategy.BANK, () => {
          throw new Error('Bank is not supported for stated info')
        })
        .with(TrueworkJsStrategy.EMPLOYMENT_STRATEGIES, () => {
          throw new Error('Employment strategies is not supported for stated info')
        })
        .with(TrueworkJsStrategy.SMART_OUTREACH, () => {
          throw new Error('Smart outreach is not supported for stated info')
        })
        .with(TrueworkJsStrategy.INSTANT, () => {
          throw new Error('Instant is not supported for stated info')
        })
        .with(TrueworkJsStrategy.IRS, () => {
          throw new Error('IRS is not supported for stated info')
        })
        .exhaustive()
    } else {
      navigate(`${routes.statedInformation}/${updatedTask.id}`, { replace: true })
      if (docsForMortgageEnabled) {
        const params = new URLSearchParams()
        params.set('q', updatedTask?.employer_name ?? '')
        const path = updatedTask ? `${routes.companySearch}/${updatedTask.id}` : routes.companySearch
        navigate(`${path}?${params.toString()}`)
      } else {
        navigate(`${routes.selectVerificationMethod}/${updatedTask.id}`, {
          state: { task: updatedTask },
        })
      }
    }
  }

  const { start_date, end_date, current_gross_base_pay, current_job, pay_unit, does_not_have_employer_address } = {
    start_date: watch('start_date'),
    end_date: watch('end_date'),
    current_gross_base_pay: watch('current_gross_base_pay'),
    current_job: watch('current_job'),
    pay_unit: watch('pay_unit'),
    does_not_have_employer_address: watch('does_not_have_employer_address'),
  }

  const onSubmit = handleSubmit(async (formValues) => {
    const { data: sessionData } = await api.clientSession.getClientSession()

    if (isSessionInTerminalState(sessionData?.state)) {
      mutateAndNavigate()
      return
    }
    // If the task has not been saved, it won't have an ID
    let taskIdToUpdate = task?.id

    if (maxEmployments === 1 && tasks.length === 1) {
      taskIdToUpdate = tasks[0].id
    }

    if (taskIdToUpdate) {
      const data = await updateTask({
        empId: taskIdToUpdate,
        body: {
          start_date: formValues.start_date ? new Date(formValues.start_date) : undefined,
          end_date: !current_job && formValues.end_date ? new Date(formValues.end_date) : undefined,
          employment_status: current_job ? EmploymentStatus.ACTIVE : undefined,
          job_title: formValues.job_title,
          employer_location_place_id: formValues?.employer_address?.place_id,
          current_gross_base_pay: Number(current_gross_base_pay?.replace(/[\\$\\,]/g, '')),
          annual_income: Number(current_gross_base_pay?.replace(/[\\$\\,]/g, '')),
          hours_worked_per_week: formValues.hours_worked_per_week
            ? Number(formValues.hours_worked_per_week)
            : undefined,
          pay_unit: formValues.pay_unit === PayUnit.OTHER ? PayUnit.ANNUALLY : formValues.pay_unit,
          employer_name: !task?.is_external_employment ? formValues.employer_name : undefined,
        },
      })

      await mutate(['all', 'tasks'])
      goToNext(data?.data)
      return
    }

    const { data, response } = await api.tasks.createDirectTask({
      employer_name: String(formValues.employer_name),
      employer_entity_id: Number(task?.id),
      start_date: new Date(formValues.start_date),
      end_date: !current_job ? new Date(formValues.end_date) : undefined,
      employment_status: current_job ? EmploymentStatus.ACTIVE : undefined,
      job_title: formValues.job_title,
      current_gross_base_pay: Number(current_gross_base_pay?.replace(/[\\$\\,]/g, '')),
      annual_income: Number(current_gross_base_pay?.replace(/[\\$\\,]/g, '')),
      employer_location_place_id: formValues?.employer_address?.place_id,
      strategy: bilEnabledStrategies.length === 1 ? bilEnabledStrategies[0] : undefined,
      hours_worked_per_week: formValues.hours_worked_per_week ? Number(formValues.hours_worked_per_week) : undefined,
      pay_unit: formValues.pay_unit === PayUnit.OTHER ? PayUnit.ANNUALLY : formValues.pay_unit,
      login_item: loginItemIds.length === 1 ? loginItemIds[0] : undefined,
    })

    if (data && response.ok) {
      handleEvent(InternalEventType.ADDED_EMPLOYER)
      await mutate(['all', 'tasks'])
      goToNext(data)
    }
  })

  useEffect(() => {
    if (current_job || !endDateEnabled) {
      if (errors.end_date) resetField('end_date')
      setValue('end_date', PRESENT_END_DATE)
      if (documentsEnabled) {
        setValue('current_job', true)
      }
    } else if (!current_job && end_date === PRESENT_END_DATE) {
      setValue('end_date', '')
    }
  }, [current_job, resetField, setValue, errors.end_date, end_date, endDateEnabled])

  useEffect(() => {
    if (!dateIsValid(start_date)) {
      setError('start_date', {
        type: 'manual',
        message: 'Invalid',
      })
    } else {
      clearErrors('start_date')
    }

    if (!dateIsValid(end_date)) {
      setError('end_date', {
        type: 'manual',
        message: 'Invalid',
      })
    } else if (!endDateIsValid(start_date, end_date)) {
      setError('end_date', {
        type: 'manual',
        message: 'Must be after start date',
      })
    } else {
      clearErrors('end_date')
    }
  }, [start_date, end_date, setError, clearErrors])

  const params = new URLSearchParams(location.search)
  const employerName = params.get('employerName')

  useEffect(() => {
    // fix this
    if (task) {
      reset({
        current_gross_base_pay: task.unverified_report?.current_gross_base_pay
          ? `${formatCurrency(
              '' + task.unverified_report.current_gross_base_pay,
              task.unverified_report?.pay_unit === PayUnit.HOURLY
            )}`
          : '',
        current_job: task.unverified_report?.is_active_employment,
        employer_address: {
          description: task.unverified_report?.employer_raw_address ?? '',
          place_id: task.unverified_report?.employer_location_place_id ?? '',
        },
        job_title: task.unverified_report?.job_title || '',
        start_date: task.unverified_report?.start_date ? formatDate(task.unverified_report.start_date) : '',
        end_date: !endDateEnabled
          ? PRESENT_END_DATE
          : task.unverified_report?.end_date
          ? formatDate(task.unverified_report.end_date)
          : '',
        employer_name: task.employer_name ?? '',
        hours_worked_per_week: task.unverified_report?.hours_worked_per_week
          ? String(task.unverified_report.hours_worked_per_week)
          : '',
        pay_unit: task.unverified_report?.pay_unit ?? PayUnit.ANNUALLY,
      })
    }

    if (employerName) {
      setValue('employer_name', employerName)
    }
  }, [task, employerName, endDateEnabled, reset, setValue])

  useEffect(() => {
    if (!task || isDirty) return

    const details = cachedEmploymentInfo[task.id]?.details

    if (!details) return

    for (const key in details) {
      const value = details[key as keyof CachedEmploymentDetails]

      setValue(key as keyof FormValues, value)
    }
  }, [task, cachedEmploymentInfo, setValue, isDirty])

  function handleClickNoEmployerAddress() {
    const nextValue = !does_not_have_employer_address
    setValue('does_not_have_employer_address', nextValue)
    if (nextValue) {
      clearErrors('employer_address')
    } else {
      setValue('employer_address', {
        description: '',
        place_id: '',
      })
    }
  }

  function clearAddressErrors(key: string) {
    const coercedKey = key as keyof FormValues
    clearErrors(coercedKey)
  }

  const taskId = task?.id || ''

  if (isLoading) {
    return <Loading />
  }

  const disabled = isSubmitting && isValid

  return (
    <>
      <WidgetHeaderWithNavigation
        title={title}
        showExitPrompt={showExitPrompt}
        exitPromptType={ExitPromptType.EMPLOYER}
        backButton={{
          visible: !shouldLoginToEntity,
          onClick: () => {
            if (taskId) {
              /*
             If the employment already exists, we don't want to go back to any page before stated info (e.g. Select Income Page), otherwise we risk creating a duplicate employment.
             Instead, navigate back to the tasks page
            */
              navigate(routes.root)
            } else {
              navigate(-1)
            }
          },
        }}
      />
      <Body data-cy={DataAttribute.POS_SCREEN_COMPANY_DETAILS} className="h-full p-5" paddingTopOverride={0}>
        <H2 className="text-left">Provide employment details</H2>
        <P className="mb-5 mt-1" variant="small">
          Please provide your best approximations.
        </P>
        <form onSubmit={onSubmit} className="flex flex-col space-y-4 bg-transparent">
          <FormErrors visible={Object.keys(errors)?.length > 0 && submitCount > 0}>
            {Object.keys(errors).map((key) => (
              <li key={key}>{getErrorLabelForKey(key as keyof FormValues, pay_unit)}</li>
            ))}
          </FormErrors>
          <Controller
            name="employer_name"
            control={control}
            rules={{
              required: { value: true, message: 'Required' },
            }}
            render={({ field: { value, name, onChange } }) => (
              <Input
                data-cy={DataAttribute.POS_EMPLOYER_NAME_INPUT}
                value={value}
                name={name}
                label="Employer Name"
                error={errors.employer_name?.message}
                disabled={disabled}
                onChange={(e) => {
                  const { value } = e.target
                  onChange(value)
                  saveDetails(taskId, { employer_name: value })
                }}
                required
              />
            )}
          />
          {endDateEnabled && (
            <div
              data-cy={DataAttribute.POS_CURRENT_JOB_CHECKBOX}
              className={cn(
                'flex cursor-pointer select-none items-center space-x-2',
                disabled && 'pointer-events-none opacity-50'
              )}
              onClick={() => {
                setValue('current_job', !current_job)
              }}
            >
              <Checkbox checked={Boolean(current_job)} label="I am currently working at this company" />
            </div>
          )}
          <div className="flex items-start space-x-4">
            <Controller
              name="start_date"
              control={control}
              rules={{
                pattern: { value: /\d{2}\/\d{2}\/\d{4}/, message: 'MM/DD/YYYY' },
                required: { value: true, message: 'Required' },
                validate: (value) => {
                  const date = new Date(value)
                  const isValid = date.toString() !== 'Invalid Date'
                  return isValid ? undefined : 'Invalid'
                },
              }}
              render={({ field: { value, name, onChange } }) => (
                <Input
                  data-cy={DataAttribute.POS_START_DATE_INPUT}
                  value={value}
                  name={name}
                  label="Start Date"
                  error={errors.start_date?.message}
                  type="dateString"
                  disabled={disabled}
                  className="flex-1"
                  onChange={(e) => {
                    const { value } = e.target
                    const formattedValue = formatDateWithSlash(value)
                    onChange(formattedValue)
                    saveDetails(taskId, { start_date: formattedValue })
                  }}
                  required
                />
              )}
            />
            <Controller
              name="end_date"
              control={control}
              rules={{
                pattern: !current_job ? { value: /\d{2}\/\d{2}\/\d{4}/, message: 'MM/DD/YYYY' } : undefined,
                required: { value: true, message: 'Required' },
                validate: (value) => {
                  if (value === PRESENT_END_DATE) return undefined
                  const isValidDate = dateIsValid(value)
                  const isValidEndDate = endDateIsValid(start_date, value)

                  if (!isValidDate) return 'Invalid'
                  if (!isValidEndDate) return 'Must be after start date'

                  return undefined
                },
              }}
              render={({ field: { value, name, onChange } }) => (
                <Input
                  data-cy={DataAttribute.POS_END_DATE_INPUT}
                  value={value}
                  name={name}
                  type="dateString"
                  label="End Date"
                  className="flex-1"
                  error={errors.end_date?.message}
                  disabled={disabled || current_job}
                  onChange={(e) => {
                    const { value } = e.target
                    const formattedValue = formatDateWithSlash(value)
                    onChange(formattedValue)
                    saveDetails(taskId, { end_date: formattedValue })
                  }}
                  required
                />
              )}
            />
          </div>
          <Controller
            name="job_title"
            control={control}
            rules={{
              required: { value: true, message: 'Required' },
            }}
            render={({ field: { value, name, onChange } }) => (
              <Input
                value={value}
                name={name}
                data-cy={DataAttribute.POS_JOB_TITLE_INPUT}
                label="Position or Job Title"
                placeholder="e.g. Sales Manager"
                error={errors.job_title?.message}
                disabled={disabled}
                onChange={(e) => {
                  const { value } = e.target
                  onChange(value)
                  saveDetails(taskId, { job_title: value })
                }}
                required
              />
            )}
          />
          <EmployerAddress
            control={control}
            noAddress={does_not_have_employer_address}
            clearErrors={clearAddressErrors}
            disabled={disabled}
            required
          />
          <div
            data-cy={DataAttribute.NO_EMPLOYER_ADDRESS_CHECKBOX}
            className={cn(
              'flex cursor-pointer select-none items-center space-x-2',
              disabled && 'pointer-events-none opacity-50'
            )}
            onClick={handleClickNoEmployerAddress}
            onKeyDown={(e) => e.key === 'Enter' && handleClickNoEmployerAddress()}
          >
            <Checkbox
              checked={Boolean(does_not_have_employer_address)}
              label="My employer doesn't have a physical address"
            />
          </div>
          <PayTypeFields
            selectedPayType={pay_unit}
            disabled={disabled}
            control={control}
            errors={errors}
            clearField={(field: keyof FormValues) => {
              resetField(field)
              clearErrors(field)
              setValue(field, '')
            }}
            required
          />
        </form>
      </Body>
      <Footer
        buttons={{
          primary: (
            <Button
              data-cy={DataAttribute.POS_ADD_EMPLOYER_SAVE_BUTTON}
              onClick={onSubmit}
              intent="primary"
              disabled={isSubmitting || isMutating || Object.keys(errors)?.length > 0}
              Icon={isSubmitting ? LoadingSpinner : undefined}
              applyCobranding
            >
              Verify this income
            </Button>
          ),
        }}
      />
    </>
  )
}
